/**
 * Venue (organization / location) requirement for events.
 *
 * WHY THIS EXISTS
 * The organization / venue selector on the create wizard's "When & Where" step was
 * OPTIONAL. That — not any Event-in-a-Box defect — is why in-person events reach
 * production with no organization link at all (measured on PROD 2026-09-09: 13 of 53
 * `range_night` events, 26 of 66 `gun_show`, and every one of the 56 legacy `show`
 * events have no row in EITHER `event_organizations` or `event_locations`). An event
 * with no venue link cannot be grouped, filtered or reported on by organization, and
 * the EIB proximity audience has no anchor to work from.
 *
 * Owner decision (2026-09-09): make it REQUIRED for in-person events.
 *
 * ONE RULE, BOTH SURFACES. EventCreateWizard (create) and EventForm (edit) each had
 * their own ad-hoc validation; the rule lives here so it cannot drift between them
 * and so it is unit-testable without rendering either form.
 *
 * WHICH FIELD SATISFIES IT depends on the event type, because the create/edit forms
 * SWAP the field in that slot: a gun show is run by a promoter (whose address is
 * explicitly NOT the show location), so those forms hide the org/location selector
 * and show the Promoter Organization picker instead. Requiring an
 * `event_organizations` row for a gun show would be unsatisfiable through the UI, so
 * for that type the promoter IS the venue link. Leaving the promoter optional would
 * also make the rule sidesteppable just by choosing "Gun Show".
 */

/** Event type whose forms swap the Promoter picker into the organization slot. */
export const PROMOTER_SLOT_EVENT_TYPES: ReadonlyArray<string> = ["gun_show"];

export type EventVenueField = "org" | "promoter";

export interface EventVenueError {
  /** Which control the operator must fill to clear this. */
  field: EventVenueField;
  message: string;
}

export interface EventVenueRequirementInput {
  /** `events.location_type` — "physical" | "virtual" | "hybrid". */
  locationType?: string | null;
  /** `events.type` — decides whether the org slot holds the promoter picker. */
  eventType?: string | null;
  /** `events.date_tbd` — a placeholder event whose venue is not yet booked. */
  dateTbd?: boolean | null;
  /** How many org/location associations are currently selected in the form. */
  orgSelectionCount: number;
  /** The promoter organization currently selected in the form, if any. */
  promoterId?: string | null;
  /**
   * GRANDFATHER SWITCH — set true when EDITING an event that was LOADED with no
   * venue link at all. Those events predate this rule; blocking their save would
   * mean an operator could not fix anything else about them (the status, the date,
   * the times) without first inventing an organization. They stay savable.
   *
   * It is deliberately keyed on what the event ARRIVED with, not on what is in the
   * form now: an event that HAS a venue link still cannot have it cleared.
   */
  grandfathered?: boolean;
}

export const VENUE_REQUIRED_ORG_MESSAGE =
  "Associate this event with an organization or location — in-person events need a " +
  "venue. Search for the organization above and click Add. (Online-only event? Set " +
  "Location Type to Virtual.)";

export const VENUE_REQUIRED_PROMOTER_MESSAGE =
  "Select the promoter organization running this show — in-person events need an " +
  "organization link. (Online-only event? Set Location Type to Virtual.)";

/**
 * True when a venue link is required for this event shape.
 *
 * REQUIRED for `physical` and `hybrid`. Hybrid is included because it has a real
 * in-person leg — and because both forms already treat physical||hybrid as the
 * in-person case for the Location Name and Location State requirements, so a
 * narrower rule here would be inconsistent with the fields right beside it.
 *
 * EXEMPT:
 *  - `virtual` — there is no venue; the Meeting URL is the location.
 *  - `date_tbd` — an unscheduled placeholder; the venue is typically not yet
 *    booked, and forcing a guess would put a WRONG organization on the event,
 *    which is worse than a gap.
 *  - an unrecognised/blank location type — fail OPEN. This is a form-level nicety,
 *    not a safety guard, and a value we do not understand must not become an
 *    unclearable block on saving.
 */
export function venueLinkRequired(
  input: Pick<EventVenueRequirementInput, "locationType" | "dateTbd">,
): boolean {
  if (input.dateTbd) {
    return false;
  }
  return input.locationType === "physical" || input.locationType === "hybrid";
}

/** Which control carries the venue link for this event type. */
export function venueFieldFor(eventType?: string | null): EventVenueField {
  return eventType && PROMOTER_SLOT_EVENT_TYPES.includes(eventType)
    ? "promoter"
    : "org";
}

/**
 * The venue validation error for the current form state, or null when it passes.
 * Pure — callers map the returned `field` onto their own error key.
 */
export function eventVenueError(
  input: EventVenueRequirementInput,
): EventVenueError | null {
  if (input.grandfathered) {
    return null;
  }
  if (!venueLinkRequired(input)) {
    return null;
  }
  const field = venueFieldFor(input.eventType);
  if (field === "promoter") {
    return (input.promoterId ?? "").trim() === ""
      ? { field, message: VENUE_REQUIRED_PROMOTER_MESSAGE }
      : null;
  }
  return input.orgSelectionCount > 0
    ? null
    : { field, message: VENUE_REQUIRED_ORG_MESSAGE };
}
